import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useMediaQuery } from 'shared/hooks/use-match-media'
import { findStep as findCourseStep } from 'modules/course/utils/course-steps'
import type {
  CourseMenuLectureInterface,
  CourseQuizInterface,
} from '../../types/course-menu-interface'
import { CourseStepEnum } from '../../types/course-step-type'
import { getCoursePageQuery } from '../../utils/get-course-page-url'
import { parseQueryId } from '../../utils/parse-query-id'
import { useGetCoursePageUrl } from '../use-course-path-url'
import useCourseSteps from '../use-course-steps'
import type { StepType } from './types'
import { getContentType } from './utils'

export default function useStepRouter(): {
  isCurrentLecture: (
    step: CourseMenuLectureInterface | CourseQuizInterface | null | undefined,
  ) => boolean
  isCurrentQuiz: (
    step: CourseMenuLectureInterface | CourseQuizInterface | null | undefined,
  ) => boolean
  isCurrentAssignment: (
    step: CourseMenuLectureInterface | CourseQuizInterface | null | undefined,
  ) => boolean
  getLectureUrl: (id: number) => string
  getQuizUrl: (id: number) => string
  getAssignmentUrl: (id: number) => string
  step: StepType | undefined
  findStepById: (id?: number) => StepType | undefined
  isPreview: boolean
  findStep: () => StepType | undefined
  redirectToStep: (
    step: StepType | null | undefined,
    { shallow }?: { shallow: boolean },
  ) => Promise<boolean> | undefined
  onNextStep: () => Promise<boolean> | undefined
  onPreviousStep: () => Promise<boolean> | undefined
  isEnd: boolean
  isStart: boolean
} {
  const router = useRouter()

  const [currentStep, setCurrentStep] = useState<StepType>()
  const [isEnd, setIsEnd] = useState<boolean>(false)
  const [isStart, setIsStart] = useState<boolean>(false)

  const isFirstRender = useRef(true)

  const coursePath = useGetCoursePageUrl()
  const { courseSteps } = useCourseSteps()
  const isLG = useMediaQuery('(min-width: 1024px)', false)

  const { lectureId: rawLectureId, quizId: rawQuizId, preview } = router.query
  const [lectureId, quizId] = [rawLectureId, rawQuizId].map(element => parseQueryId(element))

  const isCurrentStep =
    (stepType: CourseStepEnum) =>
    (step: CourseMenuLectureInterface | CourseQuizInterface | undefined | null): boolean => {
      const isMatchingType = stepType === currentStep?.type

      if (!isMatchingType) return false

      return !!(step && step.id === currentStep?.id)
    }

  const getStepUrl =
    (stepType: CourseStepEnum) =>
    (id: number): string =>
      `/school/course/${coursePath}/${stepType}/${id}`

  const isStepAccessible = (step: StepType): boolean => {
    return (
      step.type === CourseStepEnum.quiz ||
      step.type === CourseStepEnum.assignment ||
      (step.type === CourseStepEnum.lecture && step.available)
    )
  }

  const isPreview = preview === 'true'

  const findStep = (): StepType | undefined => findCourseStep(courseSteps)

  const redirectToStep = (
    step: StepType | null | undefined,
    { shallow }: { shallow: boolean } = { shallow: true },
  ): Promise<boolean> | undefined => {
    if (!step || !coursePath) return

    return router.push(
      getCoursePageQuery({
        coursePath,
        type: step.type,
        id: step.id,
      }),
      undefined,
      { shallow },
    )
  }

  const getNextStep = (): StepType | undefined => {
    if (isEnd) return

    const currentStepIndex = courseSteps.findIndex(
      step => step.type === currentStep?.type && step.id === currentStep?.id,
    )

    const nextStep = courseSteps
      .slice(currentStepIndex + 1)
      .find(element => isStepAccessible(element))

    return nextStep
  }

  const getPreviousStep = (): StepType | undefined => {
    if (isStart) return

    const currentStepIndex = courseSteps.findIndex(
      step => step.type === currentStep?.type && step.id === currentStep?.id,
    )
    const previousStep = courseSteps
      .slice(0, currentStepIndex)
      .toReversed()
      .find(element => isStepAccessible(element))

    return previousStep
  }

  const onNextStep = (): Promise<boolean> | undefined => {
    const step = getNextStep()
    return redirectToStep(step, { shallow: false })
  }

  const onPreviousStep = (): Promise<boolean> | undefined => {
    const step = getPreviousStep()
    return redirectToStep(step, { shallow: false })
  }

  const findStepById = (id?: number): StepType | undefined =>
    id ? courseSteps?.find(step => step?.id === id) : undefined

  useEffect(() => {
    if (router.asPath) {
      const stepType = getContentType(router.asPath) as CourseStepEnum
      const id =
        stepType === CourseStepEnum.lecture || stepType === CourseStepEnum.assignment
          ? lectureId
          : quizId
      const step = courseSteps.find(s => s.type === stepType && s.id === id)

      if (step) {
        setCurrentStep(step)
      }
    }
  }, [router.asPath, courseSteps])

  useEffect(() => {
    if (currentStep) {
      const currentStepIndex = courseSteps.findIndex(
        step => step.type === currentStep.type && step.id === currentStep.id,
      )

      const hasPrevious = courseSteps.some((step, i) => i < currentStepIndex && step.available)
      const hasNext = courseSteps.some((step, i) => i > currentStepIndex && step.available)

      setIsStart(!hasPrevious)
      setIsEnd(!hasNext)
    }
  }, [courseSteps, currentStep])

  useEffect(() => {
    if (currentStep?.id) {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      requestAnimationFrame(() => {
        if (isLG) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          window.scrollTo({ top: 69, behavior: 'smooth' })
        }
      })
    }
  }, [currentStep?.id])

  return {
    isCurrentLecture: isCurrentStep(CourseStepEnum.lecture),
    isCurrentQuiz: isCurrentStep(CourseStepEnum.quiz),
    isCurrentAssignment: isCurrentStep(CourseStepEnum.assignment),
    getLectureUrl: getStepUrl(CourseStepEnum.lecture),
    getQuizUrl: getStepUrl(CourseStepEnum.quiz),
    getAssignmentUrl: getStepUrl(CourseStepEnum.assignment),
    step: currentStep,
    findStepById,
    isPreview,
    findStep,
    redirectToStep,
    onNextStep,
    onPreviousStep,
    isEnd,
    isStart,
  }
}
